import { Fragment, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { UploadSimple, FileXls, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Admin uploads cards TO a card_vendor. Manual paste or Excel.
 *  On upload → sum(card_limit) * (1 - discount%/100) auto-debits vendor wallet.
 */
export default function AdminCardUploadDialog({ open, onOpenChange, vendorId, vendorName, defaultDiscount, onDone }) {
  const [mode, setMode] = useState('manual');
  const [rawText, setRawText] = useState('');
  const [discount, setDiscount] = useState(defaultDiscount ?? 0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [allowDupes, setAllowDupes] = useState(false);
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [entryDate, setEntryDate] = useState(todayISO());  // YYYY-MM-DD, back-datable

  const reset = () => {
    setRawText(''); setFile(null); setPreview(null); setAllowDupes(false); setDiscount(defaultDiscount ?? 0); setEntryDate(todayISO());
  };

  // ─── Common success handler for both manual + excel uploads ────────
  const handleUploadResponse = (data) => {
    if (data?.status === 'duplicates_found') {
      const dupCount = data.existing_duplicates?.length || 0;
      toast.warning(`${dupCount} cards pehle se exist karte hain. "Skip duplicates" checkbox tick karke re-upload karein.`);
      setPreview({
        ...(preview || {}),
        parsed: data.parsed || preview?.parsed,
        count: data.parsed_count || preview?.count,
        total_face: data.total_face ?? preview?.total_face,
        existing_duplicates: data.existing_duplicates,
        within_batch_duplicates: data.within_batch_duplicates,
      });
      // Auto-check the skip checkbox for the user's convenience
      setAllowDupes(true);
      return false;
    }
    if (data?.status === 'ok') {
      // Soft warning when all cards were duplicates (0 new inserted)
      if (data.all_duplicates || data.inserted_count === 0) {
        toast.warning(`0 new cards insert hue — ${data.skipped_existing || 0} sab already DB me the.`);
        reset();
        onOpenChange(false);
        onDone?.();
        return true;
      }
      toast.success(`${data.inserted_count} cards uploaded. Debit: ${fmt(data.debit_amount)}`);
      reset();
      onOpenChange(false);
      onDone?.();
      return true;
    }
    return false;
  };

  const doPreview = async () => {
    if (!rawText.trim()) { toast.error('Paste card text first'); return; }
    setBusy(true);
    try {
      const r = await api.post('/vendor-cards/parse-preview', { vendor_id: vendorId, raw_text: rawText });
      setPreview(r.data);
      if (!r.data.count) toast.error('Koi card parse nahi hua');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Parse failed');
    } finally { setBusy(false); }
  };

  const doExcelPreview = async () => {
    if (!file) { toast.error('Excel file select karein'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('vendor_id', vendorId);
      fd.append('discount_percent', String(Number(discount) || 0));
      fd.append('allow_duplicates', String(allowDupes));
      fd.append('dry_run', 'true');
      if (entryDate) fd.append('entry_date', entryDate);
      const r = await api.post('/vendor-cards/upload/excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // dry_run returns parsed[] + counters — populate the preview table
      const d = r.data || {};
      setPreview({
        parsed: d.parsed || [],
        count: d.parsed_count ?? (d.parsed || []).length,
        total_face: d.total_face || 0,
        existing_duplicates: d.existing_duplicates || [],
        within_batch_duplicates: d.within_batch_duplicates || [],
      });
      const wouldInsert = d.would_insert ?? d.parsed_count ?? (d.parsed || []).length;
      if (!wouldInsert) {
        toast.warning(`Koi naya card insert nahi hoga — ${d.skipped_existing || 0} sab already DB me hain.`);
      } else {
        toast.success(`Preview ready — ${wouldInsert} nayi card entries upload hongi.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const doManualUpload = async () => {
    if (!rawText.trim()) { toast.error('Paste card text first'); return; }
    setBusy(true);
    try {
      const r = await api.post('/vendor-cards/upload/manual', {
        vendor_id: vendorId, raw_text: rawText,
        discount_percent: Number(discount) || 0,
        allow_duplicates: allowDupes,
        entry_date: entryDate || null,
      });
      handleUploadResponse(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const doExcelUpload = async () => {
    if (!file) { toast.error('Excel file select karein'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('vendor_id', vendorId);
      fd.append('discount_percent', String(Number(discount) || 0));
      fd.append('allow_duplicates', String(allowDupes));
      if (entryDate) fd.append('entry_date', entryDate);
      const r = await api.post('/vendor-cards/upload/excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleUploadResponse(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Excel upload failed');
    } finally { setBusy(false); }
  };

  const effectiveDebit = preview ? (preview.total_face || 0) * (1 - (Number(discount) || 0) / 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-card-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadSimple size={20} weight="duotone" className="text-indigo-600" />
            Upload Cards → {vendorName}
          </DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex gap-2">
          {[
            ['manual', 'Paste Text', UploadSimple],
            ['excel', 'Excel Upload', FileXls],
          ].map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setMode(k); setPreview(null); }}
              className={`flex-1 p-3 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 ${mode === k ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-white'}`}
              data-testid={`card-upload-mode-${k}`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* Discount + Entry Date + duplicate toggle row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Discount % (admin decides per batch)</Label>
            <Input
              type="number" step="0.01" min={0} max={100}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              data-testid="card-upload-discount"
            />
            <div className="text-[10px] text-gray-500 mt-1">0% bhi de sakte ho. Debit = face × (1 − discount%).</div>
          </div>
          <div>
            <Label className="flex items-center gap-1">
              Entry Date
              {entryDate && entryDate !== todayISO() && (
                <span className="text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5">BACK-DATED</span>
              )}
            </Label>
            <div className="flex gap-1">
              <Input
                type="date"
                value={entryDate}
                max={todayISO()}
                onChange={(e) => setEntryDate(e.target.value || todayISO())}
                className="flex-1"
                data-testid="card-upload-entry-date"
              />
              {entryDate !== todayISO() && (
                <button
                  type="button"
                  onClick={() => setEntryDate(todayISO())}
                  className="text-[10px] font-semibold px-2 border border-gray-300 rounded hover:bg-gray-50"
                  title="Reset to today"
                >Today</button>
              )}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              Default aaj ki date. Ledger + cards is date pe stamp honge.
            </div>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={allowDupes} onChange={(e) => setAllowDupes(e.target.checked)} />
              Skip duplicates and force-upload rest
            </label>
          </div>
        </div>

        {/* Manual mode */}
        {mode === 'manual' && (
          <div className="space-y-2">
            <Label>Paste card details (any format — blank line separates cards)</Label>
            <Textarea
              rows={8}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={'Example:\nRAVI RAJ\n5321 3502 6437 5577\nCVV2 - 609\nExpiry - 04/26 (mm/yy)\nCard Limit Rs.20200\n\n6083 6702 0184 1217\n10/27\n083\nPin eE3cS1tD0\nRs 4988'}
              data-testid="card-upload-textarea"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={doPreview} disabled={busy}>Preview Parse</Button>
              <Button onClick={doManualUpload} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="card-upload-manual-submit">
                {busy ? 'Uploading...' : 'Upload & Debit Wallet'}
              </Button>
            </div>
          </div>
        )}

        {/* Excel mode */}
        {mode === 'excel' && (
          <div className="space-y-2">
            <Label>Upload File — supports <span className="font-bold text-emerald-700">.xlsx, .xls, .csv, .txt</span> (Excel columns: Card Holder · Card Number · CVV · Expiry · Password · PIN · Limit. WhatsApp-pasted text bhi same upload se chalega — system khud parse karega)</Label>
            <Input type="file" accept=".xlsx,.xls,.csv,.txt" onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); }} data-testid="card-upload-excel-file" />
            {file && <div className="text-[11px] text-gray-500">{file.name}</div>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={doExcelPreview} disabled={busy || !file} data-testid="card-upload-excel-preview">
                {busy ? 'Parsing…' : 'Preview Parse'}
              </Button>
              <Button onClick={doExcelUpload} disabled={busy || !file} className="bg-indigo-600 hover:bg-indigo-700" data-testid="card-upload-excel-submit">
                {busy ? 'Uploading...' : 'Upload & Debit Wallet'}
              </Button>
            </div>
          </div>
        )}

        {/* Preview table */}
        {preview && preview.parsed && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-indigo-50 px-3 py-2 text-xs font-semibold flex justify-between">
              <span>Parsed: {preview.count} cards</span>
              <span>Total Face: {fmt(preview.total_face)} • Effective Debit @ {discount}%: {fmt(effectiveDebit)}</span>
            </div>
            <div className="overflow-x-auto max-h-60">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase">
                  <tr>
                    <th className="px-2 py-1 text-left">Holder</th>
                    <th className="px-2 py-1 text-left">Card (last 4)</th>
                    <th className="px-2 py-1 text-left">CVV</th>
                    <th className="px-2 py-1 text-left">Expiry</th>
                    <th className="px-2 py-1 text-left">PIN</th>
                    <th className="px-2 py-1 text-right">Limit</th>
                    <th className="px-2 py-1 text-left">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.parsed.map((p) => (
                    <Fragment key={p.card_number}>
                    <tr className={`border-t ${p.is_duplicate_existing ? 'bg-red-50' : p.is_duplicate_within_batch ? 'bg-amber-50' : ''}`}>
                      <td className="px-2 py-1">{p.card_holder || '—'}</td>
                      <td className="px-2 py-1 font-mono">****{p.card_number_last4}</td>
                      <td className="px-2 py-1">{p.cvv || '—'}</td>
                      <td className="px-2 py-1">{p.expiry || '—'}</td>
                      <td className="px-2 py-1">{p.pin || '—'}</td>
                      <td className="px-2 py-1 text-right">{fmt(p.card_limit)}</td>
                      <td className="px-2 py-1">
                        {p.is_duplicate_existing && <span className="text-red-700 font-semibold text-[10px] flex items-center gap-1"><Warning size={11} /> DB DUPE</span>}
                        {p.is_duplicate_within_batch && !p.is_duplicate_existing && <span className="text-amber-700 font-semibold text-[10px]">BATCH DUPE</span>}
                        {!p.is_duplicate_existing && !p.is_duplicate_within_batch && <span className="text-emerald-700 text-[10px] flex items-center gap-1"><CheckCircle size={11} /> OK</span>}
                      </td>
                    </tr>
                    {p.is_duplicate_existing && p.dup_info && (
                      <tr className="bg-red-50/60 border-t border-red-200/60">
                        <td colSpan={7} className="px-2 py-1.5 text-[10px] text-red-900" data-testid={`dupe-info-${p.card_number_last4}`}>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                            <span>↳ Already in <b>{p.dup_info.vendor_name || '—'}</b></span>
                            {p.dup_info.uploaded_at && (
                              <span>• Uploaded: <b>{new Date(p.dup_info.uploaded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</b></span>
                            )}
                            {p.dup_info.uploaded_by_name && <span>• By: <b>{p.dup_info.uploaded_by_name}</b></span>}
                            {p.dup_info.card_holder && p.dup_info.card_holder !== p.card_holder && (
                              <span>• Original holder: <b>{p.dup_info.card_holder}</b></span>
                            )}
                            {typeof p.dup_info.card_limit === 'number' && p.dup_info.card_limit !== p.card_limit && (
                              <span>• Original limit: <b>{fmt(p.dup_info.card_limit)}</b></span>
                            )}
                            <span>• Status: {p.dup_info.is_used
                              ? <b className="text-orange-700">USED{p.dup_info.used_by_name ? ` by ${p.dup_info.used_by_name}` : ''}</b>
                              : <b className="text-emerald-700">UNUSED</b>}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {(preview.existing_duplicates?.length > 0 || preview.within_batch_duplicates?.length > 0) && (
              <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 text-xs text-amber-900">
                ⚠️ {preview.existing_duplicates?.length || 0} cards pehle se DB me hain
                {preview.within_batch_duplicates?.length > 0 && `, ${preview.within_batch_duplicates.length} within-batch duplicate`}.
                {allowDupes
                  ? ' "Skip duplicates" ON hai — upload karo, ye cards skip ho jayenge aur baaki insert honge.'
                  : ' "Skip duplicates" checkbox ON karo aur phir Upload dabao.'}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
